/**
 * Table state persistence helper.
 *
 * Persists Material React Table view state (filters / global search / sorting /
 * column layout) to localStorage so it survives navigation and page reloads.
 *
 * Storage is a single versioned key, org-namespaced then keyed per table:
 *   cvTableState = {
 *     v: 1,
 *     orgs: {
 *       [organisationId]: {
 *         "auditList": { columnVisibility, columnOrder, columnPinning, globalFilter },
 *         "review:expectedSeen": { columnFilters, globalFilter, sorting, ... }
 *       }
 *     }
 *   }
 *
 * The namespace is the **organisation id**, not its name — a rename must not
 * orphan every saved view, and two organisations sharing a display name must
 * not share one.
 *
 * `v` is a schema version. The persisted slices are raw Material React Table
 * shapes, so an MRT upgrade can change what a `columnFilters` entry looks like.
 * Bump `SCHEMA_VERSION` whenever that happens and every stored view is dropped
 * on the next read instead of being fed back into the table.
 *
 * NOTE: all five audit-list status tabs (Draft / Scheduled / In progress /
 * Closed / Archived) **deliberately share the single `"auditList"` key** so a
 * controller's filters and search carry across the tabs. That is intended
 * behaviour — do not "fix" it into a per-tab key.
 *
 * Pure and hook-free — `orgId` is always passed in so this can be used both
 * inside hooks and in `useState(() => ...)` initializers.
 */

const STORAGE_KEY = "cvTableState";
const SCHEMA_VERSION = 1;

/**
 * Read and parse the org map. Returns {} on any failure, and also whenever the
 * stored schema version does not match — that is how a shape change discards
 * stale views rather than restoring them into MRT.
 */
const readAll = () => {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (!raw) return {};
        const parsed = JSON.parse(raw);
        if (!parsed || typeof parsed !== "object") return {};
        if (parsed.v !== SCHEMA_VERSION) return {};
        return parsed.orgs && typeof parsed.orgs === "object"
            ? parsed.orgs
            : {};
    } catch (_) {
        return {};
    }
};

/** Write the org map back, stamped with the current schema version. */
const writeAll = (orgs) => {
    localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify({ v: SCHEMA_VERSION, orgs })
    );
};

/** Namespace keys are object keys, so a numeric org id must be stringified. */
const bucketFor = (orgId) =>
    orgId === null || orgId === undefined || orgId === ""
        ? null
        : String(orgId);

/**
 * Read the persisted state for a single table.
 * @returns the stored slice map, or null when nothing is stored / no org.
 */
export const readTableState = (key, orgId) => {
    const org = bucketFor(orgId);
    if (!key || !org) return null;
    return readAll()[org]?.[key] ?? null;
};

/**
 * Shallow-merge `patch` (a partial slice map) into a table's stored state.
 * Merging at the slice level lets callers persist e.g. { globalFilter } and
 * { columnOrder } independently without clobbering each other. No-op when
 * there is no key or no org (avoids polluting an undefined org bucket).
 */
export const writeTableState = (key, patch, orgId) => {
    const org = bucketFor(orgId);
    if (!key || !org) return;
    try {
        const all = readAll();
        const bucket = all[org] || {};
        const current = bucket[key] || {};
        all[org] = { ...bucket, [key]: { ...current, ...patch } };
        writeAll(all);
    } catch (_) {
        // storage unavailable / quota exceeded — persistence is best-effort
    }
};

/** Remove a table's stored state (escape hatch for schema resets). */
export const clearTableState = (key, orgId) => {
    const org = bucketFor(orgId);
    if (!key || !org) return;
    try {
        const all = readAll();
        if (all[org]) {
            delete all[org][key];
            writeAll(all);
        }
    } catch (_) {}
};

/**
 * Drop every saved view for every org on this device.
 *
 * Called on logout: a saved view carries the user's own column filters and
 * global search text (a VIN, a registration, a dealer name), and the org
 * bucket is shared by everyone who signs into that org on this machine — so it
 * must not survive a sign-out onto the next user.
 */
export const clearAllTableState = () => {
    try {
        localStorage.removeItem(STORAGE_KEY);
    } catch (_) {}
};

export default {
    readTableState,
    writeTableState,
    clearTableState,
    clearAllTableState
};
